import { angular2react } from 'angular2react';
import { type auto, type IComponentOptions } from 'angular';
import { type ComponentType } from 'react';
import { type AnyObject } from '@Types';
import { angularInjectorProvider } from 'Injector';
// https://github.com/bcherny/angular2react-demos/blob/master/multi-file/src/lazyInjector.js

/**
 * Angular2react needs an $injector, but it doesn't actually invoke $injector.get
 * until we invoke ReactDOM.render. We can take advantage of this to provide the
 * "lazy" injector below to React components created with angular2react, as a way
 * to avoid component ordering issues.
 *
 * @see https://github.com/coatue-oss/angular2react/issues/12
 */

// state

function getLazyInjector() {
    const lazyInjector = {
        get $injector() {
            return {
                get get() {
                    const injector = angularInjectorProvider.requireInjector();
                    return injector.get.bind(injector);
                },
            };
        },
    };
    return lazyInjector;
}

export function angularDirectiveToReact<T extends AnyObject = AnyObject>(
    directiveName: string,
    directiveDefinitionOrBindings: AnyObject,
) {
    const bindings = directiveDefinitionOrBindings.scope || directiveDefinitionOrBindings;
    const mockComponent = {
        bindings,
    } as IComponentOptions;
    const lazyInjector = getLazyInjector();

    return angular2react<T>(directiveName, mockComponent, lazyInjector.$injector as auto.IInjectorService);
}

// Use ComponentType with 'any' props to allow storing components with different prop types
// oxlint-disable-next-line @typescript-eslint/no-explicit-any
const angularDirectiveRegistry: Record<string, ComponentType<any>> = {};

// useAngularDirectiveToReact will lazy-instantiate the component. You only HAVE to use this
// if the
export function useAngularDirectiveToReact<T extends AnyObject = AnyObject>(
    directiveName: string,
    directiveDefinitionOrBindings: AnyObject,
): ReturnType<typeof angularDirectiveToReact<T>> {
    const directive = angularDirectiveRegistry[directiveName];
    if (directive) {
        return directive as unknown as ReturnType<typeof angularDirectiveToReact<T>>;
    }
    const newDirective = angularDirectiveToReact<T>(directiveName, directiveDefinitionOrBindings);
    // oxlint-disable-next-line @typescript-eslint/no-explicit-any
    angularDirectiveRegistry[directiveName] = newDirective as unknown as ComponentType<any>;
    return newDirective;
}

export default angularDirectiveToReact;
